import { GeocodingDto } from 'services/shipment/shipmentService.dto';

import { LocationDetailsDto, LocationSource } from './locationService.dto';

const COUNTRY = 'country';
const STATE = 'administrative_area_level_1';
const CITY_TYPES = ['sublocality_level_1', 'locality', 'administrative_area_level_3'];
const ZIP = 'postal_code';

const extractComponent = (
  components: google.maps.GeocoderAddressComponent[],
  types: string[],
  useShortName = false
): string => {
  const component = types.map(type => components.find(comp => comp.types.includes(type))).find(Boolean);

  if (!component) return '';
  return useShortName ? component.short_name : component.long_name;
};

const readLatLng = (location?: google.maps.LatLng | google.maps.LatLngLiteral): { lat?: number; lng?: number } => {
  if (!location) return {};
  if (typeof (location as google.maps.LatLng).lat === 'function') {
    const latLng = location as google.maps.LatLng;
    return { lat: latLng.lat(), lng: latLng.lng() };
  }
  const literal = location as google.maps.LatLngLiteral;
  return { lat: literal.lat, lng: literal.lng };
};

export const extractLocationDetails = (
  result: google.maps.GeocoderResult | google.maps.places.PlaceResult,
  source: LocationSource,
  coords?: { lat: number; lng: number }
): LocationDetailsDto => {
  const components = result.address_components || [];
  const fromGeometry = readLatLng(result.geometry?.location);
  const latitude = coords?.lat ?? fromGeometry.lat;
  const longitude = coords?.lng ?? fromGeometry.lng;

  return {
    latitude,
    longitude,
    countryCode: extractComponent(components, [COUNTRY], true),
    state: extractComponent(components, [STATE], true),
    city: extractComponent(components, CITY_TYPES),
    zipCode: extractComponent(components, [ZIP]),
    locationSource: source,
  };
};

export const formatLocationLabel = (location?: LocationDetailsDto): string => {
  if (!location?.city) return '';
  if (!location.state) return location.city;
  const zip = location.zipCode ? `\u00a0${location.zipCode}` : '';
  return `${location.city}, ${location.state}${zip}`;
};

export const isSupportedCountry = (location: LocationDetailsDto) =>
  !location.countryCode || location.countryCode.toUpperCase() === 'US';

export const toGeocodingDto = (location: LocationDetailsDto): GeocodingDto => ({
  zipCode: location.zipCode || '',
  city: location.city || '',
  countryCode: location.countryCode || 'US',
  state: { code: location.state },
  coordinates: {
    latitude: location.latitude || 0,
    longitude: location.longitude || 0,
  },
});

export const fromUserLocation = (location?: {
  lat?: number;
  lon?: number;
  zip?: string;
  city?: string;
  state?: string;
  country?: string;
  source?: LocationDetailsDto['locationSource'];
}): LocationDetailsDto | undefined => {
  if (!location?.lat || !location?.lon) return undefined;
  return {
    latitude: location.lat,
    longitude: location.lon,
    zipCode: location.zip,
    city: location.city,
    state: location.state,
    countryCode: location.country === 'United States' ? 'US' : location.country,
    locationSource: location.source,
  };
};
